'use client';

import React from 'react';

import { Box, BoxProps } from './box';
import { Text } from './text';
import { Icon } from './icon';

export type AlertStatus = 'success' | 'error' | 'warning' | 'info';

// Alert Root Component
export interface AlertRootProps extends BoxProps {
  status?: AlertStatus;
  variant?: 'subtle' | 'solid' | 'left-accent' | 'top-accent';
  children: React.ReactNode;
}

// Alert Indicator (Icon) Component
export interface AlertIndicatorProps extends BoxProps {
  status?: AlertStatus;
  indicatorSize?: string | number;
}

// Alert Title Component
export interface AlertTitleProps extends BoxProps {
  children: React.ReactNode;
}

// Alert Description Component
export interface AlertDescriptionProps extends BoxProps {
  children: React.ReactNode;
}

const statusConfig = {
  success: {
    icon: 'CheckCircle',
    colorScheme: {
      bg: '#F0FFF4',
      border: '#9AE6B4',
      text: '#22543D',
      icon: '#38A169',
    },
  },
  error: {
    icon: 'XCircle',
    colorScheme: {
      bg: '#FED7D7',
      border: '#FEB2B2',
      text: '#742A2A',
      icon: '#E53E3E',
    },
  },
  warning: {
    icon: 'AlertTriangle',
    colorScheme: {
      bg: '#FFFBEB',
      border: '#F6E05E',
      text: '#744210',
      icon: '#D69E2E',
    },
  },
  info: {
    icon: 'Info',
    colorScheme: {
      bg: '#EBF8FF',
      border: '#90CDF4',
      text: '#2A4365',
      icon: '#3182CE',
    },
  },
};

const AlertRoot = React.forwardRef<HTMLDivElement, AlertRootProps>(
  ({ status = 'info', variant = 'subtle', children, style: userStyle, ...props }, ref) => {
    const config = statusConfig[status as keyof typeof statusConfig];

    const variantStyles = {
      subtle: {
        backgroundColor: config.colorScheme.bg,
        borderColor: config.colorScheme.border,
        color: config.colorScheme.text,
        border: '1px solid',
      },
      solid: {
        backgroundColor: config.colorScheme.icon,
        color: 'white',
      },
      'left-accent': {
        backgroundColor: config.colorScheme.bg,
        borderLeft: `4px solid ${config.colorScheme.icon}`,
        color: config.colorScheme.text,
      },
      'top-accent': {
        backgroundColor: config.colorScheme.bg,
        borderTop: `4px solid ${config.colorScheme.icon}`,
        color: config.colorScheme.text,
      },
    };

    return (
      <Box
        ref={ref}
        role="alert"
        padding={4}
        borderRadius="md"
        display="flex"
        alignItems="center"
        style={{
          gap: '12px',
          ...variantStyles[variant as keyof typeof variantStyles],
          ...userStyle,
        }}
        {...props}
      >
        {children}
      </Box>
    );
  }
);

AlertRoot.displayName = 'AlertRoot';

const AlertIndicator = React.forwardRef<HTMLDivElement, AlertIndicatorProps>(
  ({ status = 'info', indicatorSize = '20px', ...props }, ref) => {
    const config = statusConfig[status as keyof typeof statusConfig];

    return (
      <Box
        ref={ref}
        flexShrink={0}
        width={indicatorSize}
        height={indicatorSize}
        display="flex"
        alignItems="center"
        justifyContent="center"
        {...props}
      >
        <Icon size="md" iconColor="current">
          {config.icon}
        </Icon>
      </Box>
    );
  }
);

AlertIndicator.displayName = 'AlertIndicator';

const AlertTitle = React.forwardRef<HTMLDivElement, AlertTitleProps>(
  ({ children, ...props }, ref) => {
    return (
      <Text ref={ref} fontWeight="bold" fontSize="lg" lineHeight="1.2" {...props}>
        {children}
      </Text>
    );
  }
);

AlertTitle.displayName = 'AlertTitle';

const AlertDescription = React.forwardRef<HTMLDivElement, AlertDescriptionProps>(
  ({ children, ...props }, ref) => {
    return (
      <Text ref={ref} fontSize="sm" lineHeight="1.4" {...props}>
        {children}
      </Text>
    );
  }
);

AlertDescription.displayName = 'AlertDescription';

// Compound component exports
export const Alert = {
  Root: AlertRoot,
  Indicator: AlertIndicator,
  Title: AlertTitle,
  Description: AlertDescription,
};

// Individual exports for direct usage
export { AlertRoot, AlertIndicator, AlertTitle, AlertDescription };

// Legacy AlertIcon export (alias for AlertIndicator)
export const AlertIcon = AlertIndicator;

// Legacy Alert export (non-compound version)
export type AlertProps = AlertRootProps;

const AlertComponent = React.forwardRef<HTMLDivElement, AlertProps>((props, ref) => (
  <AlertRoot ref={ref} {...props} />
));

AlertComponent.displayName = 'Alert';

export default AlertComponent;
